/**
 * SLAM Design System — Inline Style Constants (UNC Carolina Athletics)
 *
 * Shorthand `C` object for use in inline React styles.
 * Maps directly to the canonical tokens in src/tokens/design-tokens.js.
 *
 * Usage:
 *   import { C, tier } from '../design-system/primitives';
 *   <div style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}` }}>
 */

export const C = {
  // Backgrounds (OLED layers)
  bg: '#000000',
  s1: '#0A0A0A',
  s2: '#111111',
  s3: '#1A1A1A',
  s4: '#222222',

  // Borders
  border: '#1F1F1F',
  borderLight: '#2A2A2A',
  borderSubtle: '#141414',

  // Carolina Blue system (primary accent)
  carolinaBlue: '#7BAFD4',
  carolinaBlueMuted: '#5A8FB2',
  carolinaBlueDim: '#1A3448',
  cb15: '#7BAFD415',
  cb08: '#7BAFD408',

  // Legacy aliases (gold → Carolina Blue)
  gold: '#7BAFD4',
  goldMuted: '#5A8FB2',
  goldDim: '#1A3448',
  g15: '#7BAFD415',
  g08: '#7BAFD408',

  // Navy system (secondary)
  uncNavy: '#13294B',
  navyLight: '#1E3A5F',

  // UNC secondary palette
  pitchBlack: '#2C2A29',
  silver: '#8D9092',
  flatGrey: '#A2AAAD',

  // Text
  text: '#F5F5F5',
  t2: '#999999',
  t3: '#555555',
  t4: '#333333',

  // Pillar colors (Carolina-adapted)
  cog: '#7BAFD4',
  char: '#C0C0C0',
  hdi: '#5A8FB2',
  tools: '#E07C24',

  // Tier colors (data-earned only)
  dev: '#FF4444',
  solid: '#7BAFD4',
  elite: '#00CC66',
  gen: '#13294B',

  // Pattern direction
  watch: '#7BAFD4',
};

/**
 * Tier classification function.
 * Returns { color, label } based on a 0-100 score.
 *
 * @param {number} v - Score value (0-100)
 * @returns {{ color: string, label: string }}
 */
export const tier = (v) => ({
  color: v >= 90 ? C.gen : v >= 70 ? C.elite : v >= 40 ? C.carolinaBlue : C.dev,
  label: v >= 90 ? 'GEN' : v >= 70 ? 'ELITE' : v >= 40 ? 'SOLID' : 'DEV',
});
